/**
 * Top-level "Vorhersage" (prediction) tab: fetches model metadata, predictions, and
 * local SHAP for the selected patient, and orchestrates the "what-if" simulation
 * sliders — one Level + one Stabilität (regularity) slider per feature, driving a
 * second AnalysisServiceGetPHQ8PredictionSpecification call with delta/regularity
 * params (see getPHQ8Prediction.ts's two-pass counterfactual logic on the backend).
 */
import { useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "../redux/store/hooks"
import DashboardWrapper from "../component/DashboardWrapper"
import RequirePatientSelection from "../component/require/RequirePatientSelection"
import notNull from "../util/notNull"
import {
    selectDashboardCurrentPatientData,
    selectDashboardSelectedPatient,
    selectDashboardSelectedValueType,
    selectDashboardValueType
} from "../redux/ProfessionalDashboard"
import {
    AnalysisServiceGetPHQ8LocalShapResponsePayload,
    AnalysisServiceGetPHQ8LocalShapSpecification,
    AnalysisServiceGetPHQ8ModelMetadataResponsePayload,
    AnalysisServiceGetPHQ8ModelMetadataSpecification,
    AnalysisServiceGetPHQ8PredictionResponsePayload,
    AnalysisServiceGetPHQ8PredictionSpecification,
    GetPatientDataCurrentSpecification,
    ValueType
} from "pokal_api"
import ModelInfoPanel from "../component/analysis/prediction/ModelInfoPanel"
import FeatureMiniChart from "../component/analysis/prediction/FeatureMiniChart"
import PredictionScore from "../component/analysis/prediction/PredictionScore"
import PredictionAnalysisPanel from "../component/analysis/prediction/PredictionAnalysisPanel"
import { dispatchThunk } from "../redux/thunk/thunkify"

type FeatureKey = "sleep_duration" | "sleep_latency" | "sleep_heart_rate" | "sleep_hrv" | "steps"

type SimulationDeltas = Record<FeatureKey, number>
type SimulationRegularity = Record<FeatureKey, number>

type FeatureControllerConfig = {
    label: string
    valueType: ValueType
    min: number
    max: number
    step: number
    unit: string
    toSimulationDelta: (value: number) => number
}

// Per-feature slider bounds/step in display units (hours, minutes, bpm, ms, steps) plus
// `toSimulationDelta`, which converts the slider's display-unit value into whatever unit
// the backend's *_delta_* params expect (seconds for sleep duration/latency, raw bpm/ms/
// steps for the rest) — see getPHQ8Prediction.ts's Pass 1 (level deltas).
const FEATURE_CONTROLLER_CONFIG: Record<FeatureKey, FeatureControllerConfig> = {
    sleep_duration: {
        label: "Schlafdauer",
        valueType: ValueType.SLEEP_DURATION,
        min: -3,
        max: 3,
        step: 0.25,
        unit: "h",
        toSimulationDelta: (value) => Math.round(value * 60 * 60)
    },
    sleep_latency: {
        label: "Schlaflatenz",
        valueType: ValueType.SLEEP_LATENCY,
        min: -30,
        max: 30,
        step: 5,
        unit: "min",
        toSimulationDelta: (value) => Math.round(value * 60)
    },
    sleep_heart_rate: {
        label: "Schlafpuls",
        valueType: ValueType.SLEEP_HEART_RATE,
        min: -15,
        max: 15,
        step: 1,
        unit: "bpm",
        toSimulationDelta: (value) => value
    },
    sleep_hrv: {
        label: "Schlaf-HRV",
        valueType: ValueType.SLEEP_HEART_RATE_VARIABILITY,
        min: -30,
        max: 30,
        step: 5,
        unit: "ms",
        toSimulationDelta: (value) => value
    },
    steps: {
        label: "Schritte",
        valueType: ValueType.STEPS,
        min: -5000,
        max: 5000,
        step: 250,
        unit: "",
        toSimulationDelta: (value) => value
    }
}

const INITIAL_SIMULATION_DELTAS: SimulationDeltas = {
    sleep_duration: 0,
    sleep_latency: 0,
    sleep_heart_rate: 0,
    sleep_hrv: 0,
    steps: 0
}

const INITIAL_SIMULATION_REGULARITY: SimulationRegularity = {
    sleep_duration: 0,
    sleep_latency: 0,
    sleep_heart_rate: 0,
    sleep_hrv: 0,
    steps: 0
}

function isFeatureKey(key: string): key is FeatureKey {
    return key in FEATURE_CONTROLLER_CONFIG
}

function PredictionView() {
    const dispatch = useAppDispatch()
    const selectedPatient = useAppSelector(selectDashboardSelectedPatient)
    const selectedValueType = useAppSelector(selectDashboardSelectedValueType)
    const currentPatientData = useAppSelector(selectDashboardCurrentPatientData)
    const [predictions, setPredictions] = useState<AnalysisServiceGetPHQ8PredictionResponsePayload | null>(null)
    const [simulatedPredictions, setSimulatedPredictions] = useState<AnalysisServiceGetPHQ8PredictionResponsePayload | null>(null)
    const [metadata, setMetadata] = useState<AnalysisServiceGetPHQ8ModelMetadataResponsePayload | null>(null)
    const [localShap, setLocalShap] = useState<AnalysisServiceGetPHQ8LocalShapResponsePayload | null>(null)
    const [loading, setLoading] = useState(false)
    const [simulationLoading, setSimulationLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [simulationDeltas, setSimulationDeltas] = useState<SimulationDeltas>(INITIAL_SIMULATION_DELTAS)
    const [simulationRegularity, setSimulationRegularity] = useState<SimulationRegularity>(INITIAL_SIMULATION_REGULARITY)
    const [isAnalyseOpen, setIsAnalyseOpen] = useState(false)

    useEffect(() => {
        if (selectedValueType !== ValueType.PHQUESTIONNAIRE) {
            dispatch(selectDashboardValueType(ValueType.PHQUESTIONNAIRE))
        }
    }, [dispatch, selectedValueType])

    useEffect(() => {
        if (!selectedPatient) return

        const fetchModelData = async () => {
            setLoading(true)
            setError(null)
            try {
                dispatchThunk(dispatch, GetPatientDataCurrentSpecification, {
                    patient: selectedPatient.id,
                    valueType: ValueType.PHQUESTIONNAIRE
                })

                const metadataResponse = await AnalysisServiceGetPHQ8ModelMetadataSpecification.request({
                    patient: selectedPatient.id
                })
                setMetadata(metadataResponse)

                const response = await AnalysisServiceGetPHQ8PredictionSpecification.request({
                    patient: selectedPatient.id,
                    horizon: metadataResponse.metadata.best_horizon,
                    lookback: metadataResponse.metadata.best_lookback,
                    today_only: false
                })
                setPredictions(response)

                const localShapResponse = await AnalysisServiceGetPHQ8LocalShapSpecification.request({
                    patient: selectedPatient.id
                })
                setLocalShap(localShapResponse)
                setSimulatedPredictions(null)
            } catch (err) {
                const clientMsg = (err as { response?: { data?: { client?: { msg?: string } } } }).response?.data?.client?.msg
                setError(clientMsg ?? (err instanceof Error ? err.message : "Fehler beim Laden der PHQ-8 Daten"))
            } finally {
                setLoading(false)
            }
        }

        fetchModelData()
    }, [selectedPatient])

    useEffect(() => {
        if (!selectedPatient || !metadata) return

        const hasSimulation =
            Object.values(simulationDeltas).some((value) => value !== 0) ||
            Object.values(simulationRegularity).some((value) => value !== 0)

        if (!hasSimulation) {
            setSimulatedPredictions(null)
            return
        }

        const fetchSimulation = async () => {
            setSimulationLoading(true)
            try {
                const simulatedResponse = await AnalysisServiceGetPHQ8PredictionSpecification.request({
                    patient: selectedPatient.id,
                    horizon: metadata.metadata.best_horizon,
                    lookback: metadata.metadata.best_lookback,
                    today_only: false,
                    sleep_duration_delta_seconds: FEATURE_CONTROLLER_CONFIG.sleep_duration.toSimulationDelta(simulationDeltas.sleep_duration),
                    sleep_latency_delta_seconds: FEATURE_CONTROLLER_CONFIG.sleep_latency.toSimulationDelta(simulationDeltas.sleep_latency),
                    sleep_heart_rate_delta_bpm: FEATURE_CONTROLLER_CONFIG.sleep_heart_rate.toSimulationDelta(simulationDeltas.sleep_heart_rate),
                    sleep_hrv_delta_ms: FEATURE_CONTROLLER_CONFIG.sleep_hrv.toSimulationDelta(simulationDeltas.sleep_hrv),
                    steps_delta: FEATURE_CONTROLLER_CONFIG.steps.toSimulationDelta(simulationDeltas.steps),
                    // "Stabilität" slider is a -100..100 % UI value; converted to the
                    // backend's regularity_factor (see getPHQ8Prediction.ts Pass 2) where
                    // 1 = no-op, <1 pulls values toward the mean (steadier), >1 exaggerates
                    // swings. +100% UI -> factor 0 (fully steady); -100% UI -> factor 2.
                    sleep_duration_regularity_factor: simulationRegularity.sleep_duration !== 0 ? 1 - simulationRegularity.sleep_duration / 100 : undefined,
                    sleep_latency_regularity_factor: simulationRegularity.sleep_latency !== 0 ? 1 - simulationRegularity.sleep_latency / 100 : undefined,
                    sleep_heart_rate_regularity_factor: simulationRegularity.sleep_heart_rate !== 0 ? 1 - simulationRegularity.sleep_heart_rate / 100 : undefined,
                    sleep_hrv_regularity_factor: simulationRegularity.sleep_hrv !== 0 ? 1 - simulationRegularity.sleep_hrv / 100 : undefined,
                    steps_regularity_factor: simulationRegularity.steps !== 0 ? 1 - simulationRegularity.steps / 100 : undefined
                })
                setSimulatedPredictions(simulatedResponse)
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to fetch simulation")
            } finally {
                setSimulationLoading(false)
            }
        }

        fetchSimulation()
    }, [
        selectedPatient,
        metadata,
        simulationDeltas,
        simulationRegularity
    ])

    const normalizedCurrentData = currentPatientData
        ? currentPatientData
              .map((item) => ({
                  date: new Date(item.date).toISOString().split("T")[0],
                  value: item.value
              }))
              .sort((left, right) => left.date.localeCompare(right.date))
        : null

    const featureSeries = predictions?.feature_series ?? []

    // Derive the earliest date that any displayed future prediction actually
    // consumes (= earliest_future_source_date - lookback).  The model's
    // lookback window is the `lookback` days *preceding* the source_date,
    // exclusive of source_date itself.  Used to trim the feature mini charts
    // so they don't show historical points only the dropped (past-target)
    // predictions would have used.
    const minFeaturePointDate = (() => {
        const todayString = new Date().toISOString().split("T")[0]
        const futurePreds = (predictions?.predictions ?? []).filter(
            (p) => p.target_date >= todayString
        )
        const lookback = metadata?.metadata.best_lookback ?? 0
        if (futurePreds.length === 0 || lookback <= 0) return null
        const earliestSource = futurePreds.reduce((a, b) =>
            a.source_date < b.source_date ? a : b
        ).source_date
        const ts = Date.parse(`${earliestSource}T00:00:00Z`)
        if (Number.isNaN(ts)) return null
        const cutoff = new Date(ts - lookback * 24 * 60 * 60 * 1000)
        return cutoff.toISOString().split("T")[0]
    })()

    return (
        <RequirePatientSelection>
            {notNull(selectedPatient) && (
                <DashboardWrapper title={`${selectedPatient.firstName} ${selectedPatient.lastName} - PHQ-8 Vorhersage`}>
                    <div className={"w-full flex flex-col space-y-6 p-4"}>
                        {loading && <div className={"text-center"}>Lädt...</div>}
                        {error && (
                            <div className={"bg-white border border-red-200 rounded-lg shadow p-6 flex flex-col items-center space-y-2 text-center"}>
                                <span className={"text-2xl"}>⚠️</span>
                                <p className={"font-semibold text-gray-800"}>{error}</p>
                                <p className={"text-sm text-gray-500"}>Bitte stellen Sie sicher, dass ein Modell in MLflow registriert ist.</p>
                            </div>
                        )}

                        {!loading && !error && (
                            <div className={"flex flex-col space-y-8"}>
                                <PredictionAnalysisPanel
                                    patientId={selectedPatient.id}
                                    isOpen={isAnalyseOpen}
                                    onToggle={() => setIsAnalyseOpen((v) => !v)}
                                    metadata={metadata?.metadata ?? null}
                                    predictions={predictions}
                                    localShap={localShap}
                                />
                                <section className={"flex flex-col space-y-4"}>
                                    <div className={"flex items-start justify-between gap-4"}>
                                        <div>
                                            <h2 className={"text-xl font-semibold text-gray-900"}>Modellinformationen</h2>
                                            <p className={"text-sm text-gray-600"}>
                                                Diese Informationen beschreiben das Machine Learning Modell, das für die Vorhersagen verwendet wird, einschließlich seiner Performance-Metriken und der wichtigsten Einflussfaktoren (SHAP-Werte).
                                            </p>
                                        </div>
                                    </div>
                                    <ModelInfoPanel
                                        metadata={metadata?.metadata ?? null}
                                        localShap={localShap}
                                    />
                                </section>

                                <section className={"flex flex-col space-y-4"}>
                                    <div>
                                        <h2 className={"text-xl font-semibold text-gray-900"}>Vorhersagen</h2>
                                        <p className={"text-sm text-gray-600"}>
                                            Prognosen auf Basis des zuvor geladenen Models.
                                        </p>
                                    </div>

                                    <div className={"bg-white p-4 rounded-lg shadow space-y-3"}>
                                        <h1 className={"font-semibold text-gray-900"}>Simulationen</h1>
                                        <p className={"text-sm text-gray-600"}>
                                            Verschiebe die Regler, um die Werte der Modell-Features zu verändern und zu sehen, wie sich die Vorhersage verändert. Die blauen Punkte in den Mini-Charts zeigen die tatsächlichen Werte der Features, die roten Punkte zeigen die simulierten Werte basierend auf den eingestellten Deltas.
                                        </p>
                                        <p className={"text-xs text-gray-600"}>Der Niveau-Regler verschiebt den Mittelwert gleichmäßig. Der Regelmäßigkeits-Regler verändert die Streuung der täglichen Werte: positiv macht sie gleichmäßiger, negativ unregelmäßiger.</p>
                                        <div className={"flex justify-end"}>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSimulationDeltas(INITIAL_SIMULATION_DELTAS)
                                                    setSimulationRegularity(INITIAL_SIMULATION_REGULARITY)
                                                }}
                                                className={"text-xs px-3 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"}
                                            >
                                                Zurücksetzen
                                            </button>
                                        </div>
                                        {simulationLoading && (
                                            <div className={"text-xs text-gray-500"}>Simulation wird berechnet...</div>
                                        )}

                                        {featureSeries.length > 0 && (
                                            <div className={"pt-2 space-y-3"}>
                                                <div className={"grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"}>
                                                    {featureSeries.map((series) => {
                                                        if (!isFeatureKey(series.key)) {
                                                            return null
                                                        }

                                                        const controller = FEATURE_CONTROLLER_CONFIG[series.key]
                                                        const sliderValue = simulationDeltas[series.key]

                                                        const regularityPct = simulationRegularity[series.key]

                                                        return (
                                                            <div key={series.key} className={"space-y-2"}>
                                                                <FeatureMiniChart
                                                                    label={series.label}
                                                                    valueType={controller.valueType}
                                                                    points={series.points}
                                                                    minDate={minFeaturePointDate}
                                                                    simulationParams={{
                                                                        levelDelta: controller.toSimulationDelta(sliderValue),
                                                                        regularityFactor: 1 - regularityPct / 100
                                                                    }}
                                                                />

                                                                <label className={"block text-xs text-gray-700"}>
                                                                    Durchschnitt ({sliderValue > 0 ? "+" : ""}
                                                                    {controller.step < 1 ? sliderValue.toFixed(1) : sliderValue}
                                                                    {controller.unit ? ` ${controller.unit}` : ""})
                                                                    <input
                                                                        type="range"
                                                                        min={controller.min}
                                                                        max={controller.max}
                                                                        step={controller.step}
                                                                        value={sliderValue}
                                                                        onChange={(event) =>
                                                                            setSimulationDeltas((prev) => ({
                                                                                ...prev,
                                                                                [series.key]: Number(event.target.value)
                                                                            }))
                                                                        }
                                                                        className={"w-full mt-1"}
                                                                    />
                                                                </label>

                                                                <label className={"block text-xs text-gray-700"}>
                                                                    Stabilität ({regularityPct > 0 ? "+" : ""}{regularityPct}%)
                                                                    <input
                                                                        type="range"
                                                                        min={-100}
                                                                        max={100}
                                                                        step={5}
                                                                        value={regularityPct}
                                                                        onChange={(event) =>
                                                                            setSimulationRegularity((prev) => ({
                                                                                ...prev,
                                                                                [series.key]: Number(event.target.value)
                                                                            }))
                                                                        }
                                                                        className={"w-full mt-1"}
                                                                    />
                                                                </label>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <PredictionScore
                                        predictions={predictions}
                                        simulatedPredictions={simulatedPredictions}
                                        metadata={metadata}
                                        currentData={normalizedCurrentData}
                                        selectedPatientfirstName={selectedPatient.firstName}
                                        selectedPatientlastName={selectedPatient.lastName}
                                    />
                                </section>

                            </div>
                        )}
                    </div>
                </DashboardWrapper>
            )}
        </RequirePatientSelection>
    )
}

export default PredictionView